import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../lib/api';
import { FluxoCaixaChart } from '@/components/financeiro/FluxoCaixaChart';
import {
  FINANCEIRO_FILTER_FONT as FILTER_FONT,
  FINANCEIRO_FILTER_INPUT as FILTER_FIELD,
  FINANCEIRO_FILTER_LABEL as FILTER_LABEL,
  FINANCEIRO_FILTER_RADIUS as FILTER_RADIUS,
  FINANCEIRO_FILTER_SHADOW as FILTER_SHADOW,
} from '@/components/financeiro/financeiroFilterFieldStyles';
import type { FluxoCaixaDto } from '@prometeus/contracts';

export const Route = createFileRoute('/w/$workspaceId/financeiro/fluxo')({
  component: FluxoCaixaPage,
});

const fmt = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function FluxoCaixaPage() {
  const { workspaceId } = Route.useParams();
  const [ano, setAno] = useState(new Date().getFullYear());

  const { data, isLoading } = useQuery<FluxoCaixaDto>({
    queryKey: ['financeiro', workspaceId, 'fluxo', ano],
    queryFn: async () => {
      const res = await api.get(`/financeiro/fluxo-caixa?ano=${ano}`);
      return res.data.data ?? res.data;
    },
  });

  return (
    <div className="space-y-5 rounded-2xl border-0 bg-[#FFFFFF] p-5 shadow-[0_0_15px_rgba(0,0,0,0.05)] outline-none ring-0 dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)] sm:p-6 md:p-6">
      <div className="min-w-0">
        <h2 className="font-sans text-base font-normal tracking-normal text-[#06141B] dark:text-foreground">
          Fluxo de caixa
        </h2>
        <p className="mt-1 max-w-2xl font-sans text-xs font-normal text-[#9BA8AB] dark:text-muted-foreground">
          Acompanhamento anual de entradas, saídas e saldo projetado.
        </p>
      </div>

      <div
        className={`border-0 bg-[#F4F5F5] p-4 ${FILTER_RADIUS} ${FILTER_SHADOW} outline-none ring-0 dark:bg-muted/40 dark:shadow-[0_0_15px_rgba(0,0,0,0.05)] sm:p-5`}
      >
        <div className={`flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end ${FILTER_FONT}`}>
          <div className="w-full min-w-[120px] max-w-[140px] space-y-1.5">
            <p className={FILTER_LABEL}>Ano</p>
            <input
              type="number"
              value={ano}
              min={2000}
              max={2100}
              onChange={(e) => setAno(Number(e.target.value))}
              className={`${FILTER_FIELD} h-10`}
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          Carregando fluxo de caixa...
        </div>
      )}

      {data && (
        <>
          <FluxoCaixaChart meses={data.meses} />

          <div className="overflow-hidden rounded-[10px] bg-[#FFFFFF] shadow-[0_0_15px_rgba(0,0,0,0.05)] dark:bg-card dark:shadow-[0_0_15px_rgba(0,0,0,0.05)]">
            <table
              className="w-full text-[12px] text-[#06141B] dark:text-foreground"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              <thead>
                <tr
                  className="border-b border-black/[0.06] dark:border-white/10"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <th className="px-3 py-3 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Mês
                  </th>
                  <th className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Entradas
                  </th>
                  <th className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Saídas
                  </th>
                  <th className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Saldo projetado
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.meses.map((m) => (
                  <tr
                    key={m.mes}
                    className="border-t border-black/[0.06] transition-colors hover:bg-[#F8FAFC]/80 dark:border-white/10 dark:hover:bg-muted/20"
                  >
                    <td className="px-3 py-2.5">{MONTH_LABELS[m.mes - 1]}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-green-600 dark:text-green-400">
                      {fmt(m.entradas)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-500 dark:text-red-400">{fmt(m.saidas)}</td>
                    <td
                      className={`px-3 py-2.5 text-right font-semibold tabular-nums ${
                        m.saldoProjetado >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                      }`}
                    >
                      {fmt(m.saldoProjetado)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
